import _ from "lodash";
import { Card, Checkbox, TextArea } from "@citi-icg-172888/icgds-react";
import { useData } from "../../services/context/DataContext";
import { BusineedsInfo, BusinessNeed } from "../../services/types/LeadRequisitionType";
import {
  BUSINESS_NEED_DESCRIPTION,
  BUSINESS_NEED_PLACEHOLDER,
} from "../../constants/textConstants";
import IconsSelect from "./IconsSelect";
import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from "react";

const BusinessNeeds = forwardRef((props, ref) => {
  const { businessNeedInfo, setBusinessNeedInfo, staticData, textAreaValue, setTextAreaValue } =
    useData();
  const [textAreaColor, setTextAreaColor] = useState<string[]>([]);
  const textAreaRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const divRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => ({
    focusErrorElement: () => {
      const errorIndex = textAreaColor.findIndex((color) => color === "#ff7a75");
      if (errorIndex !== -1 && textAreaRefs.current[errorIndex]) {
        textAreaRefs.current[errorIndex]?.focus();
      }
    },
  }));

  useEffect(() => {
    if (divRef.current) {
      divRef.current.focus();
    }
    if (window.innerWidth < 768 && window.parent.location.href.includes("contact-us")) {
      window.parent.scroll(0, 0);
    } else if (window.parent.location.href.includes("contact-us")) {
      window.parent.scroll({ top: 0, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    const icons = document.querySelectorAll(".lmn-control-icon.lmnicon-check");
    icons.forEach((icon) => {
      icon.setAttribute("aria-hidden", "true");
    });
  }, []);

  const onCheckedChange = (event: any, val: string) => {
    if (event.type === "keydown" && event.key == "Enter") {
      event.preventDefault();
      event.target.checked = !event.target.checked;
    }
    const checked = event.target.checked;
    const businessNeeds: BusinessNeed[] =
      businessNeedInfo && businessNeedInfo?.businessNeeds !== undefined
        ? [...businessNeedInfo.businessNeeds]
        : [];
    const existingIndex = businessNeeds.findIndex((item) => item.title === val);

    if (checked) {
      if (existingIndex === -1) {
        // Add new entry if it doesn't exist
        businessNeeds.push({
          title: val,
          description: "",
          checked: true,
        });
      } else {
        // Update the existing entry
        businessNeeds[existingIndex].checked = true;
      }
    } else {
      // Remove the entry if unchecked
      if (existingIndex !== -1) {
        businessNeeds.splice(existingIndex, 1);
      }
    }

    setBusinessNeedInfo((prevData) => ({
      ...prevData,
      businessNeeds: businessNeeds,
    }));
  };

  const onBlurHandler = (event: React.FocusEvent<HTMLTextAreaElement>, index: number) => {
    const newTextAreaColor = [...textAreaColor];
    newTextAreaColor[index] = event.target.value.length < 80 ? "#ff7a75" : "#a4acaf";
    setTextAreaColor(newTextAreaColor);
  };

  return (
    <fieldset style={{border: "none"}}>
      <legend id="business-needs-label" className="visually-hidden">
        Business Needs
      </legend>
      {/* Container with role="group" and aria-labelledby */}
      <div className="lmn-dnca-w-l-100" role="group" aria-labelledby="business-needs-label">
        {_.map(staticData.businessNeeds, (option: BusineedsInfo, index: number) => {
          let defaultValue = false;
          if (businessNeedInfo?.businessNeeds.some((item) => item.title === option.title)) {
            defaultValue = true;
          }
          return (
            <div key={index} ref={divRef}>
              <Card className="lmn-my-5" id="CCBBusinessNeed">
                <div className="lmn-d-flex">
                  <IconsSelect type={option.type} />
                  <div className="lmn-ml-16px lmn-align-content-2xl-center lmn-align-content-xl-center lmn-align-content-lg-center lmn-align-content-md-center lmn-align-content-sm-center">
                    <label
                      className="lmn-text-color-desc lmn-mb-0 lmn-font-size-18"
                      htmlFor={`textarea-${index}`}
                    >
                      {option.title}
                    </label>
                  </div>
                  <div className="lmn-mt-4 lmn-ml-sm-64px lmn-ml-2xl-64px lmn-ml-xl-64px lmn-ml-lg-64px lmn-ml-md-64px lmn-ml-md-auto lmn-ml-lg-auto lmn-ml-xl-auto lmn-ml-2xl-auto lmn-ml-sm-auto">
                    <Checkbox
                      defaultChecked={defaultValue}
                      checked={option?.checked}
                      onChange={(e: any) => onCheckedChange(e, option.title)}
                      onKeyDown={(e: any) => onCheckedChange(e, option.title)}
                      aria-label={option.title}
                      aria-describedby={`textarea-${index}`}
                      aria-expanded={option?.checked}
                      tabIndex={0}
                    ></Checkbox>
                  </div>
                </div>
                {businessNeedInfo?.businessNeeds.some((item) => item.title === option.title) && (
                  <div id="business_needs_textarea" className="lmn-mt-3 lmn-ml-8 lmn-w-75">
                    <div>
                      <label className="lmn-text-color-desc" htmlFor={`textarea-${index}`}>
                        <span className="required-icon">* </span>Details (between 80-1,000 characters)
                      </label>
                      <TextArea
                        id={`textarea-${index}`}
                        aria-required={true}
                        aria-describedby={`textarea-${index}-info`}
                        ref={(el) => (textAreaRefs.current[index] = el)}
                        value={
                          businessNeedInfo?.businessNeeds?.find((item) => item.title === option.title)
                            ?.description ?? ""
                        }
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                          const updatedBusinessNeeds = [...(businessNeedInfo?.businessNeeds ?? [])];
                          const businessNeedIndex = updatedBusinessNeeds.findIndex(
                            (item) => item.title === option.title
                          );
                          if (businessNeedIndex !== -1) {
                            updatedBusinessNeeds[businessNeedIndex].description = e.target.value;
                            setBusinessNeedInfo((prevData) => ({
                              ...prevData,
                              businessNeeds: updatedBusinessNeeds,
                            }));
                          }
                          const newTextAreaValue = [...textAreaValue];
                          newTextAreaValue[index] = e.target.value.length;
                          setTextAreaValue(newTextAreaValue);
                          const newTextAreaColor = [...textAreaColor];
                          newTextAreaColor[index] =
                            e.target.value.length >= 80
                              ? "#a4acaf"
                              : newTextAreaColor[index] === "#ff7a75"
                                ? "#ff7a75"
                                : "#a4acaf";
                          setTextAreaColor(newTextAreaColor);
                        }}
                        rows={4}
                        cols={20}
                        placeholder={BUSINESS_NEED_PLACEHOLDER}
                        minLength={80}
                        maxLength={1000}
                        aria-label={`${option.title} is ${businessNeedInfo?.businessNeeds?.find((item) => item.title === option.title)
                          ?.description ?? ""}`}
                        onBlur={(e) => onBlurHandler(e, index)}
                      />
                      <div className="lmn-d-flex">
                        {
                          <div id={`textarea-${index}-info`}>
                            <span
                              className="lmn-hint-text-color lmn-font-size-xs lmn-text-wrap lmn-flex-grow-1 lmn-mr-auto"
                              style={{
                                color:
                                  textAreaColor?.[index] === undefined
                                    ? "#a4acaf"
                                    : textAreaColor?.[index],
                              }}
                            >
                              {BUSINESS_NEED_DESCRIPTION}
                            </span>
                            <span className="lmn-hint-text-color lmn-font-size-xs lmn-pl-4 lmn-ml-lg-auto lmn-ml-xl-auto lmn-ml-2xl-auto lmn-text-nowrap">
                              {textAreaValue?.[index] > 0 ? textAreaValue?.[index] : 0} / 1000
                            </span>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
});

export default BusinessNeeds;
